/**
 * 업무 프로젝트(PMS) J3b — 프로젝트 설정 페이지(정보·멤버·항목·노트).
 *
 * 업무 프로젝트 설정 페이지(/tasks/:projectId/settings) 본문. TaskPmPage 가 project 를 불러온 뒤에만
 * 마운트한다. 왼쪽 세로 메뉴 220 + 오른쪽 본문 카드 880(잔디 배치).
 * - 편집은 담당자(manager)이고 종료되지 않은 프로젝트에서만. 그 밖에는 편집 요소만 비활성이고
 *   탭 전환·뒤로는 늘 된다. 위험 구역(종료/다시 열기/영구 삭제)은 담당자면 종료 상태에서도 쓴다.
 * - 🔴 실패 문구를 지어내지 않는다 — 서버 detail 을 그대로 보여 준다(409 보호 규칙이 전부 서버에 있다).
 * - 🔴 멤버를 바꾼 뒤에는 응답 상세를 쓰지 않고 getProject 로 다시 불러온다. 응답의 my_role 은
 *   바꾸기 전 값이라, 내가 나를 강등·내보냈을 때 화면 권한이 틀린다.
 * - 🔴 선택지 API 는 항목 하나(또는 목록)만 돌려준다 — 상세가 아니다. 그래서 변경 뒤 재조회한다.
 */
import { useEffect, useRef, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { errorText } from './errorText';
import { alertDialog, confirmDialog, promptDialog } from '../../components/AppDialog';
import useIsMobile from '../../hooks/useIsMobile';
import { COLORS, RADIUS, badge, btn, card, input } from '../../utils/uiStyles';
import TaskPmAutomations from './TaskPmAutomations';

// TaskPmPage 의 같은 규칙(그쪽 상수는 export 되지 않아 여기 한 벌 더 둔다 — 서버 _clean_key 와 같다).
const KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;
const KEY_HINT = '영문 대문자로 시작하는 영숫자 2~10자';
const DEFAULT_OPTION_COLOR = '#8A94A6';       // backend/models/task_pm.py DEFAULT_OPTION_COLOR
const SEARCH_DEBOUNCE_MS = 300;
const NOT_FOUND = '프로젝트를 찾을 수 없거나 볼 권한이 없습니다.';

const TABS = [
  { value: 'info', label: '정보' },
  { value: 'members', label: '멤버' },
  { value: 'options', label: '항목' },
  { value: 'notes', label: '노트' },
  { value: 'automation', label: '자동화' },
];
const OPTION_KINDS = [
  { value: 'status', label: '상태' },
  { value: 'type', label: '유형' },
  { value: 'priority', label: '우선순위' },
];
const ROLE_LABEL = { manager: '담당자', member: '멤버' };

// (J5b0) 노트 수정·삭제 권한 — 값은 서버 PM_NOTE_POLICIES(backend/models/task_pm.py)와 같다.
const NOTE_POLICIES = [
  { value: 'all', label: '담당자와 멤버 모두' },
  { value: 'author_or_manager', label: '노트를 만든 사람과 프로젝트 담당자만' },
];

// 🔴 늘 문자열을 받는다 — 422 의 detail 은 배열이라 그대로 그리면 React #31 로 화면이 통째로 깨진다.
const requestError = (e, fallback = '요청에 실패했습니다.') => errorText(e, fallback);
const statusOf = (e) => e?.response?.status;

const row = { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderBottom: `1px solid ${COLORS.borderSoft}` };
const label = { display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '10px' };
const colorBox = { width: '44px', height: '28px', padding: 0, border: `1px solid ${COLORS.inputBorder}`, borderRadius: '8px', background: COLORS.surface, flex: '0 0 auto' };

const emptyOptionForm = () => ({ label: '', color: DEFAULT_OPTION_COLOR, isDone: false });

export default function TaskPmProjectSettings({ project, onChanged, onDeleted, onClose }) {
  const mobile = useIsMobile();
  const [tab, setTab] = useState('info');
  const [kind, setKind] = useState('status');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // (#1303) 멤버 추가·항목 추가는 다시 읽기까지 잠근다 — busy 와 나눈다(섞으면 정보 저장 단추까지 잠긴다).
  const [memberBusy, setMemberBusy] = useState(false);
  const [optionBusy, setOptionBusy] = useState(false);

  // (#1303) 상세를 바꾸거나 다시 읽는 요청의 «보낸 순서» — 늦게 온 옛 상세가 새 상세를 덮지 않게.
  const seqRef = useRef(0);          // 상세를 바꾸거나 다시 읽는 요청을 보낼 때마다 1씩 — 보낸 순서
  const appliedSeqRef = useRef(0);   // 위로 올린 상세 중 가장 늦게 보낸 요청의 번호
  const staleAfterRef = useRef(null);// 옛 응답을 버린 순간의 seqRef — 그보다 늦게 보낸 응답이 들어오기 전까지 «낡음»
  const resyncRef = useRef({ running: false, again: false });
  const touchedRef = useRef(new Set()); // 사용자가 건드린 정보 칸 — 낡은 동안에는 서버 값과 같아도 싣는다
  const aliveRef = useRef(true);
  // 🔴 늦게 끝나는 비동기(다시 읽기의 again 재호출)가 옛 렌더의 onChanged·project.id 를 잡지 않게 한다.
  const onChangedRef = useRef(onChanged);
  const projectIdRef = useRef(project.id);
  onChangedRef.current = onChanged;
  projectIdRef.current = project.id;
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  const nextSeq = () => { seqRef.current += 1; return seqRef.current; };

  const [info, setInfo] = useState(() => ({
    name: project.name || '', key: project.key || '', color: project.color || '#4C7DD9',
    visibility: project.visibility || 'private', description: project.description || '',
  }));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [newRole, setNewRole] = useState('member');
  const [optionForm, setOptionForm] = useState(emptyOptionForm);
  const [labelEdit, setLabelEdit] = useState({ id: null, value: '' });
  const [notePolicy, setNotePolicy] = useState(() => project.note_edit_policy || 'all');

  const isManager = project.my_role === 'manager';
  const canEdit = isManager && !project.closed;
  const members = project.members || [];
  const options = (project.options || {})[kind] || [];
  const notice = !isManager
    ? '담당자만 바꿀 수 있습니다.'
    : (project.closed ? '종료된 프로젝트입니다. 다시 열어야 설정을 바꿀 수 있습니다.' : '');

  // 저장·종료·다시 열기 뒤 새 상세가 내려오면 정보 입력값을 그 값으로 다시 채운다.
  useEffect(() => {
    setInfo({
      name: project.name || '', key: project.key || '', color: project.color || '#4C7DD9',
      visibility: project.visibility || 'private', description: project.description || '',
    });
    setNotePolicy(project.note_edit_policy || 'all');
    touchedRef.current = new Set();      // 서버 값으로 다시 채웠으니 «건드림»도 끝
  }, [project]);

  // 멤버 검색은 타이핑이 멈춘 뒤에 보낸다.
  useEffect(() => {
    const keyword = query.trim();
    if (!keyword) { setResults([]); return undefined; }
    let alive = true;
    const timer = setTimeout(() => {
      taskPmAPI.searchUsers(keyword)
        .then((res) => { if (alive) setResults(Array.isArray(res?.data) ? res.data : []); })
        .catch(() => { if (alive) setResults([]); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);

  // 상세 한 벌을 위로 올린다 — 더 늦게 보낸 요청의 응답이 이미 올라갔으면 올리지 않고 false.
  const applyProject = (data, seq) => {
    if (!data || typeof data !== 'object') return true;     // 부모가 어차피 버린다 — 다시 읽기 대상 아님
    if (seq < appliedSeqRef.current) return false;
    appliedSeqRef.current = seq;
    if (staleAfterRef.current !== null && seq > staleAfterRef.current) staleAfterRef.current = null;
    onChangedRef.current(data);
    return true;
  };

  // 옛 응답을 버렸다 = 화면이 서버와 어긋났을 수 있다 → 한 번 다시 읽는다(겹치지 않게 · 실패는 조용히).
  const resync = () => {
    if (!aliveRef.current) return;
    const r = resyncRef.current;
    if (r.running) { r.again = true; return; }
    r.running = true;
    const seq = nextSeq();
    taskPmAPI.getProject(projectIdRef.current)
      .then((res) => { applyProject(res?.data, seq); })
      .catch(() => {})
      .finally(() => {
        r.running = false;
        if (r.again && aliveRef.current) { r.again = false; resync(); }
      });
  };

  // 🔴 상세를 받는 모든 성공 경로는 여기로 — seq 는 그 요청을 «보내기 직전»에 nextSeq() 로 받은 번호.
  const accept = (data, seq) => {
    if (!applyProject(data, seq)) { staleAfterRef.current = seqRef.current; resync(); }
  };

  // 🔴 멤버·선택지를 바꾼 뒤의 공통 마무리 — 상세를 다시 읽어 올린다.
  //    404·403 이면 내가 이 프로젝트에서 빠진 것이니 목록으로 보낸다.
  const reload = async () => {
    const seq = nextSeq();
    try {
      const res = await taskPmAPI.getProject(project.id);
      accept(res?.data, seq);
    } catch (e) {
      const code = statusOf(e);
      if (code === 404 || code === 403) { onDeleted(); return; }
      if (seq < appliedSeqRef.current) return;               // 더 늦게 보낸 응답이 이미 올라갔다 — 옛 실패 문구는 안 띄운다
      setError(requestError(e, NOT_FOUND));
    }
  };

  // ── 정보
  const keyValid = KEY_PATTERN.test(info.key);
  const canSaveInfo = canEdit && Boolean(info.name.trim()) && keyValid && !busy;
  // 정보 칸을 고치면 «건드린 칸»으로 적어 둔다(낡은 동안 저장할 때 쓴다).
  const editInfo = (field, value) => {
    touchedRef.current.add(field);
    setInfo({ ...info, [field]: value });
  };

  const saveInfo = async () => {
    if (!canSaveInfo) return;
    // 바뀐 필드만 보낸다 — 키를 그대로 다시 보내면 자기 자신과의 중복 검사를 거쳐 느려질 뿐이다.
    // 🔴 옛 응답을 버린 뒤(낡음)에는 project 가 서버보다 낡았을 수 있다 — 사용자가 건드린 칸은 같아 보여도 싣는다.
    const stale = staleAfterRef.current !== null;
    const touched = (field) => stale && touchedRef.current.has(field);
    const body = {};
    if (info.name.trim() !== (project.name || '') || touched('name')) body.name = info.name.trim();
    if (info.key !== (project.key || '') || touched('key')) body.key = info.key;
    if (info.color !== (project.color || '') || touched('color')) body.color = info.color;
    if (info.visibility !== (project.visibility || '') || touched('visibility')) body.visibility = info.visibility;
    if (info.description !== (project.description || '') || touched('description')) body.description = info.description;
    if (Object.keys(body).length === 0) { setError(''); return; }
    setBusy(true);
    setError('');
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.updateProject(project.id, body);
      accept(res?.data, seq);
    } catch (e) {
      setError(requestError(e, '프로젝트 정보를 저장하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  // ── 노트 권한 — 바뀌었을 때만 보낸다.
  const canSaveNotePolicy = canEdit && !busy && notePolicy !== (project.note_edit_policy || 'all');
  const saveNotePolicy = async () => {
    if (!canSaveNotePolicy) return;
    setBusy(true);
    setError('');
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.updateProject(project.id, { note_edit_policy: notePolicy });
      accept(res?.data, seq);
    } catch (e) {
      setError(requestError(e, '노트 권한을 저장하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const runDanger = async (message, call) => {
    if (!(await confirmDialog(message))) return;
    setError('');
    try {
      const seq = nextSeq();
      const res = await call();
      accept(res?.data, seq);
    } catch (e) {
      setError(requestError(e, '요청에 실패했습니다.'));
    }
  };

  const removeProject = async () => {
    const typed = await promptDialog('삭제하려면 프로젝트 이름을 그대로 입력하세요');
    if (typed === null || typed === undefined) return;   // 취소
    setError('');
    try {
      await taskPmAPI.deleteProject(project.id, typed);
      onDeleted();
    } catch (e) {
      setError(requestError(e, '프로젝트를 삭제하지 못했습니다.'));
    }
  };

  // ── 멤버
  const changeRole = async (userId, role) => {
    setError('');
    try {
      await taskPmAPI.updateMember(project.id, userId, role);
      await reload();
    } catch (e) {
      alertDialog(requestError(e, '역할을 바꾸지 못했습니다.'));
    }
  };

  const kickMember = async (member) => {
    const who = member.name || member.email || '이 사용자';
    if (!(await confirmDialog(`${who} 님을 이 프로젝트에서 내보낼까요?`))) return;
    setError('');
    try {
      await taskPmAPI.removeMember(project.id, member.user_id);
      await reload();
    } catch (e) {
      alertDialog(requestError(e, '멤버를 내보내지 못했습니다.'));
    }
  };

  const addMember = async () => {
    if (!picked || memberBusy) return;
    setMemberBusy(true);
    setError('');
    try {
      await taskPmAPI.addMember(project.id, picked.id, newRole);
      setPicked(null);
      setQuery('');
      setResults([]);
      // 🔴 다시 읽기까지 잠근다(reload 는 예외를 밖으로 내지 않는다). 다시 읽기 응답이 버려지면
      //    다시 읽기(resync)를 기다리지 않고 풀린다.
      await reload();
    } catch (e) {
      alertDialog(requestError(e, '멤버를 추가하지 못했습니다.'));
    } finally {
      setMemberBusy(false);
    }
  };

  // ── 항목(상태·유형·우선순위)
  const patchOption = async (optionId, body) => {
    setError('');
    try {
      await taskPmAPI.updateOption(optionId, body);
      await reload();
    } catch (e) {
      setError(requestError(e, '항목을 바꾸지 못했습니다.'));
    }
  };

  const commitLabel = (option) => {
    if (labelEdit.id !== option.id) return;
    const next = labelEdit.value.trim();
    setLabelEdit({ id: null, value: '' });
    if (!next || next === option.label) return;
    patchOption(option.id, { label: next });
  };

  const moveOption = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= options.length) return;
    const ids = options.map((o) => o.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setError('');
    try {
      await taskPmAPI.reorderOptions(project.id, kind, ids);
      await reload();
    } catch (e) {
      setError(requestError(e, '항목 순서를 바꾸지 못했습니다.'));
    }
  };

  const removeOption = async (option) => {
    if (!(await confirmDialog(`'${option.label}' 항목을 지울까요?`))) return;
    setError('');
    try {
      await taskPmAPI.deleteOption(option.id);
      await reload();
    } catch (e) {
      // 사용 중·마지막 항목·마지막 완료 상태는 서버가 409 로 막는다(문구도 서버 것을 쓴다).
      setError(requestError(e, '항목을 지우지 못했습니다.'));
    }
  };

  const addOption = async () => {
    const text = optionForm.label.trim();
    if (!text || optionBusy) return;
    const body = { kind, label: text, color: optionForm.color };
    if (kind === 'status') body.is_done = optionForm.isDone;
    setOptionBusy(true);
    setError('');
    try {
      await taskPmAPI.addOption(project.id, body);
      setOptionForm(emptyOptionForm());
      await reload();                    // 🔴 다시 읽기까지 잠근다(멤버 추가와 같다)
    } catch (e) {
      setError(requestError(e, '항목을 추가하지 못했습니다.'));
    } finally {
      setOptionBusy(false);
    }
  };

  // ───────────────────────────────────────────── 탭 내용
  // 🔴 정보 칸·노트 권한은 저장 중(busy)에도 잠근다 — 응답 뒤 위 effect 가 서버 값으로 다시 채우므로
  //    그사이 고친 값은 덮인다. 저장은 단추로만 시작해 초점이 이미 단추에 있으니 disabled 로 잠근다.
  const infoTab = (
    <>
      <label style={label}>
        이름
        <input aria-label="이름" value={info.name} disabled={!canEdit || busy}
          onChange={(e) => editInfo('name', e.target.value)}
          style={{ ...input, marginTop: '4px' }} />
      </label>
      <label style={{ ...label, marginBottom: '4px' }}>
        키
        <input aria-label="키" value={info.key} disabled={!canEdit || busy}
          onChange={(e) => editInfo('key', e.target.value.toUpperCase())}
          style={{ ...input, marginTop: '4px' }} />
      </label>
      <p style={{ margin: '0 0 10px', fontSize: '11px', color: info.key && !keyValid ? COLORS.dangerText : COLORS.textMuted }}>{KEY_HINT}</p>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <label style={{ fontSize: '12px', color: COLORS.textSub }}>
          색
          <input type="color" aria-label="색" value={info.color} disabled={!canEdit || busy}
            onChange={(e) => editInfo('color', e.target.value)}
            style={{ ...colorBox, display: 'block', marginTop: '4px', width: '56px', height: '32px' }} />
        </label>
        <label style={{ flex: 1, fontSize: '12px', color: COLORS.textSub }}>
          공개 범위
          <select aria-label="공개 범위" value={info.visibility} disabled={!canEdit || busy}
            onChange={(e) => editInfo('visibility', e.target.value)}
            style={{ ...input, marginTop: '4px' }}>
            <option value="private">비공개</option>
            <option value="public">공개</option>
          </select>
        </label>
      </div>
      <label style={label}>
        설명
        <textarea aria-label="설명" rows={3} value={info.description} disabled={!canEdit || busy}
          onChange={(e) => editInfo('description', e.target.value)}
          style={{ ...input, marginTop: '4px', resize: 'vertical' }} />
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={saveInfo} disabled={!canSaveInfo}
          style={{ ...btn('primary', 'sm'), opacity: canSaveInfo ? 1 : 0.5 }}>저장</button>
      </div>

      {isManager && (
        <section aria-label="위험 구역" style={{ ...card, marginTop: '16px', padding: '12px 14px', background: COLORS.dangerSoft, border: `1px solid ${COLORS.danger}22` }}>
          <h4 style={{ margin: '0 0 6px', fontSize: '13px', color: COLORS.dangerText }}>위험 구역</h4>
          <p style={{ margin: '0 0 10px', fontSize: '12px', color: COLORS.textSub }}>
            종료하면 업무를 더 쓸 수 없고, 영구 삭제는 되돌릴 수 없습니다.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {project.closed
              ? (
                <button type="button" style={btn('secondary', 'sm')}
                  onClick={() => runDanger('이 프로젝트를 다시 열까요?', () => taskPmAPI.reopenProject(project.id))}>다시 열기</button>
              )
              : (
                <button type="button" style={btn('secondary', 'sm')}
                  onClick={() => runDanger('이 프로젝트를 종료할까요?', () => taskPmAPI.closeProject(project.id))}>프로젝트 종료</button>
              )}
            <button type="button" onClick={removeProject} style={btn('danger', 'sm')}>영구 삭제</button>
          </div>
        </section>
      )}
    </>
  );

  const membersTab = (
    <>
      <ul style={{ listStyle: 'none', margin: '0 0 14px', padding: 0 }}>
        {members.map((m) => (
          <li key={m.user_id} style={row}>
            <span style={{ flex: 1, minWidth: 0, fontSize: '13px' }}>
              {m.name || m.email}
              <span style={{ color: COLORS.textMuted, fontSize: '11px', marginLeft: '6px' }}>{m.email}</span>
            </span>
            <select aria-label={`${m.name || m.email} 역할`} value={m.role} disabled={!canEdit}
              onChange={(e) => changeRole(m.user_id, e.target.value)}
              style={{ ...input, width: '110px' }}>
              <option value="manager">담당자</option>
              <option value="member">멤버</option>
            </select>
            <button type="button" aria-label={`${m.name || m.email} 내보내기`} disabled={!canEdit}
              onClick={() => kickMember(m)}
              style={{ ...btn('secondary', 'sm'), opacity: canEdit ? 1 : 0.5 }}>내보내기</button>
          </li>
        ))}
        {members.length === 0 && <li style={{ fontSize: '12px', color: COLORS.textSub }}>멤버가 없습니다.</li>}
      </ul>

      <section aria-label="멤버 추가" style={{ ...card, padding: '12px 14px' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '13px' }}>멤버 추가</h4>
        {/* 🔴 추가 중에는 검색·결과·역할·«추가»를 다시 읽기까지 잠근다 — 응답이 비우는 입력을 그사이 고치지 않게. */}
        <input type="search" aria-label="멤버 검색" placeholder="이름 또는 이메일" value={query} disabled={!canEdit || memberBusy}
          onChange={(e) => setQuery(e.target.value)} style={{ ...input, marginBottom: '8px' }} />
        <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0, maxHeight: '160px', overflowY: 'auto' }}>
          {results.map((u) => {
            // 🔴 상세의 멤버는 user_id, 검색 결과는 id 다 — 비교할 때 섞지 않는다.
            const already = members.some((m) => m.user_id === u.id);
            const chosen = picked?.id === u.id;
            return (
              <li key={u.id}>
                <button type="button" aria-pressed={chosen} disabled={!canEdit || already || memberBusy}
                  onClick={() => setPicked(u)}
                  style={{
                    ...btn(chosen ? 'accent' : 'secondary', 'sm'), width: '100%', textAlign: 'left',
                    marginBottom: '4px', opacity: already ? 0.5 : 1,
                  }}>
                  {u.name || u.email}
                  <span style={{ color: COLORS.textMuted, marginLeft: '6px' }}>{u.email}</span>
                  {already && <span style={{ ...badge('default'), marginLeft: '6px' }}>멤버</span>}
                </button>
              </li>
            );
          })}
          {query.trim() && results.length === 0 && (
            <li style={{ fontSize: '12px', color: COLORS.textSub }}>찾는 사용자가 없습니다.</li>
          )}
        </ul>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: COLORS.textSub, flex: 1, minWidth: 0 }}>
            {picked ? `선택: ${picked.name || picked.email}` : '추가할 사람을 고르세요.'}
          </span>
          <select aria-label="추가할 역할" value={newRole} disabled={!canEdit || memberBusy}
            onChange={(e) => setNewRole(e.target.value)} style={{ ...input, width: '110px' }}>
            <option value="member">멤버</option>
            <option value="manager">담당자</option>
          </select>
          <button type="button" onClick={addMember} disabled={!canEdit || !picked || memberBusy}
            style={{ ...btn('primary', 'sm'), opacity: canEdit && picked && !memberBusy ? 1 : 0.5 }}>추가</button>
        </div>
      </section>
    </>
  );

  const optionsTab = (
    <>
      <div role="tablist" aria-label="항목 종류" style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        {OPTION_KINDS.map((k) => (
          <button key={k.value} type="button" role="tab" aria-selected={kind === k.value} disabled={optionBusy}
            onClick={() => { setKind(k.value); setLabelEdit({ id: null, value: '' }); setOptionForm(emptyOptionForm()); }}
            style={kind === k.value ? btn('primary', 'sm') : btn('secondary', 'sm')}>{k.label}</button>
        ))}
      </div>

      <ul style={{ listStyle: 'none', margin: '0 0 14px', padding: 0 }}>
        {options.map((o, index) => (
          <li key={o.id} style={row}>
            <input type="color" aria-label={`${o.label} 색`} value={o.color || DEFAULT_OPTION_COLOR} disabled={!canEdit}
              onChange={(e) => patchOption(o.id, { color: e.target.value })} style={colorBox} />
            <input aria-label={`${o.label} 이름`} disabled={!canEdit}
              value={labelEdit.id === o.id ? labelEdit.value : o.label}
              onChange={(e) => setLabelEdit({ id: o.id, value: e.target.value })}
              onBlur={() => commitLabel(o)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={{ ...input, flex: 1, minWidth: '80px' }} />
            {kind === 'status' && (
              <label style={{ fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap' }}>
                <input type="checkbox" aria-label={`${o.label} 완료로 집계`} checked={Boolean(o.is_done)} disabled={!canEdit}
                  onChange={(e) => patchOption(o.id, { is_done: e.target.checked })} />
                완료로 집계
              </label>
            )}
            <button type="button" aria-label={`${o.label} 위로`} disabled={!canEdit || index === 0}
              onClick={() => moveOption(index, -1)} style={btn('secondary', 'sm')}>위로</button>
            <button type="button" aria-label={`${o.label} 아래로`} disabled={!canEdit || index === options.length - 1}
              onClick={() => moveOption(index, 1)} style={btn('secondary', 'sm')}>아래로</button>
            <button type="button" aria-label={`${o.label} 삭제`} disabled={!canEdit}
              onClick={() => removeOption(o)} style={btn('danger', 'sm')}>삭제</button>
          </li>
        ))}
      </ul>

      <section aria-label="항목 추가" style={{ ...card, padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {/* 🔴 추가 중에는 종류 탭·새 항목 칸·«항목 추가»를 다시 읽기까지 잠근다 — 응답이 폼을 비운다. */}
        <input type="color" aria-label="새 항목 색" value={optionForm.color} disabled={!canEdit || optionBusy}
          onChange={(e) => setOptionForm({ ...optionForm, color: e.target.value })} style={colorBox} />
        <input aria-label="새 항목 이름" placeholder="항목 이름" value={optionForm.label} disabled={!canEdit || optionBusy}
          onChange={(e) => setOptionForm({ ...optionForm, label: e.target.value })}
          style={{ ...input, flex: 1, minWidth: '140px' }} />
        {kind === 'status' && (
          <label style={{ fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap' }}>
            <input type="checkbox" aria-label="새 항목 완료로 집계" checked={optionForm.isDone} disabled={!canEdit || optionBusy}
              onChange={(e) => setOptionForm({ ...optionForm, isDone: e.target.checked })} />
            완료로 집계
          </label>
        )}
        <button type="button" onClick={addOption} disabled={!canEdit || !optionForm.label.trim() || optionBusy}
          style={{ ...btn('primary', 'sm'), opacity: canEdit && optionForm.label.trim() && !optionBusy ? 1 : 0.5 }}>항목 추가</button>
      </section>
    </>
  );

  // 🔴 라디오는 <label> 이 감싼다 → 접근 이름이 라벨 글자다. 안내 문구는 J5a 서버 규칙과 같다
  //    (만들기·복제는 멤버 모두 · 종료 프로젝트는 409 · 공개 프로젝트의 비멤버는 보기만).
  const notesTab = (
    <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
      <legend style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', padding: 0 }}>노트 수정·삭제 권한</legend>
      <p style={{ margin: '0 0 10px', fontSize: '12px', color: COLORS.textSub }}>
        이 설정은 노트 수정·삭제에만 적용됩니다. 노트 보기·새 노트 만들기·복제는 이 설정과 관계없이 담당자와 멤버 모두 할 수 있습니다. 공개 프로젝트는 멤버가 아니어도 노트를 볼 수 있고, 종료된 프로젝트에서는 만들기·수정·삭제가 모두 막힙니다.
      </p>
      {NOTE_POLICIES.map((p) => (
        <label key={p.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', marginBottom: '6px' }}>
          <input type="radio" name="note-policy" value={p.value} checked={notePolicy === p.value}
            disabled={!canEdit || busy} onChange={() => setNotePolicy(p.value)} />
          {p.label}
        </label>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
        <button type="button" onClick={saveNotePolicy} disabled={!canSaveNotePolicy}
          style={{ ...btn('primary', 'sm'), opacity: canSaveNotePolicy ? 1 : 0.5 }}>노트 권한 저장</button>
      </div>
    </fieldset>
  );

  // 왼쪽 세로 메뉴(잔디 220 · sticky) — 모바일에서는 가로로 눕고 넘치면 좌우로 스크롤한다.
  const menuStyle = mobile
    ? { display: 'flex', gap: '4px', overflowX: 'auto' }
    : { flex: '0 0 220px', width: '220px', position: 'sticky', top: '16px', display: 'flex', flexDirection: 'column', gap: '2px' };
  // 메뉴 항목(잔디 높이 38) — 고른 것만 연한 강조 배경으로 칠한다(게이트웨이 색).
  const menuItem = (on) => ({
    height: '38px', boxSizing: 'border-box', padding: '0 12px', border: 'none', borderRadius: RADIUS.md,
    fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap',
    ...(mobile ? {} : { width: '100%', textAlign: 'left' }),
    ...(on
      ? { background: COLORS.primarySoft, color: COLORS.primaryDark, fontWeight: 600 }
      : { background: 'transparent', color: COLORS.text, fontWeight: 500 }),
  });

  return (
    <section aria-label="프로젝트 설정">
      {/* 머리(잔디 127) — «뒤로» → 제목 → 부제 세로 묶음. */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: '8px', minHeight: '127px' }}>
        <button type="button" aria-label="뒤로" onClick={onClose} className="gw-btn gw-btn--quiet"
          style={{ ...btn('quiet', 'sm'), height: '32px', boxSizing: 'border-box' }}>← 뒤로</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h1 className="gw-h1" style={{ margin: 0 }}>{project.name} — 설정</h1>
          {project.closed && <span style={badge('warn')}>종료</span>}
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textSub }}>
          내 역할: {ROLE_LABEL[project.my_role] || '읽기'}
        </p>
      </div>

      {/* 2단(잔디 220 + 간격 24 + 본문 880) — 모바일은 위아래로 쌓는다. */}
      <div style={{
        display: 'flex', flexDirection: mobile ? 'column' : 'row',
        alignItems: mobile ? 'stretch' : 'flex-start', gap: mobile ? '12px' : '24px',
      }}>
        <div role="tablist" aria-label="설정 항목" aria-orientation={mobile ? 'horizontal' : 'vertical'} style={menuStyle}>
          {TABS.map((t) => (
            <button key={t.value} type="button" role="tab" aria-selected={tab === t.value}
              onClick={() => { setTab(t.value); setError(''); }}
              style={menuItem(tab === t.value)}>{t.label}</button>
          ))}
        </div>

        <div style={{ ...card, flex: '1 1 auto', minWidth: 0, maxWidth: mobile ? 'none' : '880px', boxSizing: 'border-box' }}>
          {notice && (
            <p style={{ margin: '0 0 10px', fontSize: '12px', color: COLORS.textSub }}>{notice}</p>
          )}
          {error && (
            <div role="alert" style={{ ...card, padding: '8px 10px', background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '10px', fontSize: '12px' }}>{error}</div>
          )}

          {tab === 'info' && infoTab}
          {tab === 'members' && membersTab}
          {tab === 'options' && optionsTab}
          {tab === 'notes' && notesTab}
          {tab === 'automation' && <TaskPmAutomations project={project} />}
        </div>
      </div>
    </section>
  );
}
